/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { ArrowLeft, Shield } from "lucide-react";
import { useEffect } from "react";

interface PrivacyPolicyProps {
  onBack: () => void;
}

export function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <img src="/docgether.png" alt="DocGether" className="h-8 w-auto rounded-lg" />
            
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-zinc-100 rounded-xl">
            <Shield className="w-6 h-6 text-zinc-700" />
          </div>
          <h1 className="text-3xl font-semibold text-zinc-900 tracking-tight">
            Privacy Policy
          </h1>
        </div>

        <div className="prose prose-zinc max-w-none">
          <p className="text-zinc-500 text-lg leading-relaxed">
            At DocGether, we take your privacy seriously. This Privacy Policy explains how we collect, 
            use, and protect your information when you use our service.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            Information We Collect
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-4">
            We collect minimal information necessary to provide our service:
          </p>
          <ul className="space-y-2 text-zinc-600 mb-6">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span><strong>Account Information:</strong> When you sign up, we collect your email address and authentication details through our secure OAuth providers (Google, GitHub).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span><strong>API Specifications:</strong> The OpenAPI/Swagger files you upload are stored securely to generate your documentation.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span><strong>Usage Data:</strong> Basic analytics to help us improve the service (page views, feature usage).</span>
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            How We Use Your Information
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-4">
            We use your information solely to:
          </p>
          <ul className="space-y-2 text-zinc-600 mb-6">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Provide and maintain the DocGether service</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Generate and host your API documentation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Improve our service based on usage patterns</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Communicate important service updates</span>
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            Data Security
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            We implement appropriate security measures to protect your data. All API specifications 
            are stored securely, and we use industry-standard encryption for data in transit and at rest. 
            We never share your API specifications with third parties or use them for any purpose other 
            than providing the DocGether service.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            Data Retention
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            We retain your API specifications for as long as your account is active or as needed to 
            provide you with our services. You can delete your specifications at any time from your 
            dashboard. If you delete your account, all associated data will be permanently removed 
            from our systems within 30 days.
          </p>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            Your Rights
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-4">
            You have the right to:
          </p>
          <ul className="space-y-2 text-zinc-600 mb-6">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Access your personal data</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Delete your account and associated data</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Export your API specifications</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 mt-2 shrink-0"></span>
              <span>Opt-out of non-essential communications</span>
            </li>
          </ul>

          <h2 className="text-xl font-semibold text-zinc-900 mt-10 mb-4">
            Changes to This Policy
          </h2>
          <p className="text-zinc-600 leading-relaxed mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any changes 
            by posting the new policy on this page and updating the effective date.
          </p>

          <div className="mt-12 p-6 bg-zinc-50 rounded-2xl border border-zinc-200">
            <p className="text-sm text-zinc-500">
              <strong>Last Updated:</strong> March 2025<br />
              If you have any questions about this Privacy Policy, please contact us at{" "}
              <a href="mailto:privacy@docgether.io" className="text-zinc-900 hover:underline">
                privacy@docgether.io
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
